import {isEmpty, isEqual, omit} from 'lodash'
import {Masters, MasterTracks, Track, TextTrack, Slide, MasterTracksOptions, MasterTrack} from 'powtoon-versioning/src/currentSchema'

interface TrackWithMaster extends Track {
  isMaster?: boolean
}

export const defaultMasters: Masters = {
  tracks: {
    back: [],
    front: []
  }
}

const convertedTracks = (tracks: Track[], slide: Slide, useOverride: boolean): TrackWithMaster[] => {
  return tracks.filter((track: Track) => {
    const trackIdTypedKey = track.id as keyof typeof slide.masterTracks
    
    if (slide?.masterTracks?.[trackIdTypedKey])
    {
      return !(slide.masterTracks[trackIdTypedKey] as MasterTracksOptions).isHidden
    }
    return true
  }).map((element: Track) => {
    if (!useOverride) {
      return element
    }

    return {
      ...element,
      isMaster: true,
      endTime: slide.duration,
      startTime: 0,
      enterEffect: {
        effectId: 'None',
        handContentId: null,
        soundEffectId: null
      },
      exitEffect: {
        effectId: 'None',
        handContentId: null,
        soundEffectId: null
      }
    }
  })
}

interface GetMergedTracksWithMasterProps  {
  tracks: Track[],
  masters: Masters,
  slide: Slide,
  useOverride: boolean
}

export const getMergedTracksWithMaster: (args: GetMergedTracksWithMasterProps) => (TrackWithMaster | Track)[] =
  ({tracks, masters, slide, useOverride = true}) => {
  
    const mergedTracks = [
      ...convertedTracks(masters.tracks.back as Track[], slide, useOverride) as Track[],
      ...tracks,
      ...convertedTracks(masters.tracks.front as Track[], slide, useOverride) as Track[]
    ]
    return mergedTracks
  }

export const filterOutMasterTracks = (tracks: TrackWithMaster[]) => isEmpty(tracks)
  ? [] :
  tracks.filter(isNotMasterTrack)

export const isMasterTrack = ({isMaster}: TrackWithMaster) => isMaster
export const isNotMasterTrack = ({isMaster}: TrackWithMaster) => !isMaster

export const cloneMasterTrackWitoutId = (masterTrack: (Track | TextTrack)) => {
  return {
    ...omit(masterTrack, 'id'),
    sprites: masterTrack.sprites.map(sprite => omit(sprite, 'id') )
  }
}

export const isMasterTrackEmpty = (masterTracks: MasterTracks) => isEmpty(masterTracks)
  || (isEmpty(masterTracks.front) && isEmpty(masterTracks.back))

export const isMasterTracksContentEqual = (original: MasterTrack, comparable: MasterTrack) => {
  const originalClone = cloneMasterTrackWitoutId(original)
  const comparableClone = cloneMasterTrackWitoutId(comparable)

  return isEqual(originalClone, comparableClone)
}

export const isMasterTrackDuplicateExists = (masterTrack: MasterTrack, {front, back}: MasterTracks) => {
  return [...front, ...back].some(
    track => masterTrack.id !== track.id && isMasterTracksContentEqual(track, masterTrack)
  )
}

export const pushUniqMasterTracks = (tracks: MasterTrack[], masterTracks: MasterTracks, target: 'front' | 'back') => {
  tracks.forEach(track => {
    if (!isMasterTrackDuplicateExists(track, masterTracks)) {
      masterTracks[target].push(track)
    }
  })
}

export const pushUniqFrontMasterTracks = (tracks: MasterTrack[], masterTracks: MasterTracks) => {
  pushUniqMasterTracks(tracks, masterTracks, 'front')
}

export const pushUniqBackMasterTracks = (tracks: MasterTrack[], masterTracks: MasterTracks) => {
  pushUniqMasterTracks(tracks, masterTracks, 'back')
}
